<template>
    <div class="sy-scrollbar">
        <div ref="wrap" :style="wStyle" :class="wClass" @scroll="handleScroll">
            <div ref="view" :style="viewStyle" :class="vClass">
                <slot />
            </div>
        </div>
        <Bar v-if="scrollbarX" :move="moveX" :size="sizeWidth" />
        <Bar v-if="scrollbarY" vertical :move="moveY" :size="sizeHeight" />
    </div>
</template>

<script>
// Reference https://github.com/noeldelgado/gemini-scrollbar/blob/master/index.js
import _ from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { scrollbarWidth } from './components/util';
import Bar from './components/bar.vue';

export default {
    name: 'SyScrollbar',
    components: { Bar },
    props: {
        /**
         * 水平滚动条,默认无水平滚动条
         */
        scrollbarX: {
            type: Boolean,
            default: false
        },
        /**
         * 垂直滚动条,默认有垂直滚动条
         */
        scrollbarY: {
            type: Boolean,
            default: true
        },
        /**
         * 包装容器样式类
         */
        wrapClass: {
            type: String,
            default: ''
        },
        /**
         * 包装容器样式对象
         */
        wrapStyle: {
            type: Object,
            default() {
                return {};
            }
        },
        /**
         * 视图样式类
         */
        viewClass: {
            type: String,
            default: ''
        },
        /**
         * 视图样式对象
         */
        viewStyle: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            // 滚动条水平滑块长度
            sizeWidth: '0',
            // 滚动条垂直滑块长度
            sizeHeight: '0',
            // 水平滑块位置
            moveX: 0,
            // 垂直滑块位置
            moveY: 0,
            // ResizeObserver实例,用于监听dom尺寸变化
            observer: false
        };
    },
    computed: {
        /**
         * 包装器引用计算值
         */
        wrap() {
            return this.$refs.wrap;
        },
        /**
         * 包装器样式类计算值
         */
        wClass() {
            return ['sy-scrollbar-wrap', this.wrapClass];
        },
        /**
         * 包装类样式对象计算值
         */
        wStyle() {
            // 计算滚动条宽度
            const gutter = scrollbarWidth();
            // 拷贝包装器样式对象,避免修改原值
            const style = _.clone(this.wrapStyle);

            const gutterWith = `calc(100% + ${gutter}px)`;

            // 包装器宽度和高度为父容器100%加滚动条宽度
            style.width = this.scrollbarY ? gutterWith : '100%';
            style.height = this.scrollbarX ? gutterWith : '100%';

            style.overflowX = this.scrollbarX ? 'scroll' : 'hidden';
            style.overflowY = this.scrollbarY ? 'scroll' : 'hidden';

            return style;
        },
        /**
         * 视图样式类计算值
         */
        vClass() {
            return ['sy-scrollbar-view', this.viewClass];
        }
    },
    /**
     * 组件渲染完成方法
     */
    mounted() {
        // 渲染完成后立即更新滚动条位置
        this.update();

        // 创建ResizeObserver实例
        this.observer = new ResizeObserver(this.handelResize);
        // 监听wrap尺寸变化,外部容器尺寸发生变化,view尺寸不变
        this.observer.observe(this.$refs.wrap);
        // 监听view尺寸变化,外部容器尺寸不变,view尺寸发生变化
        this.observer.observe(this.$refs.view);
    },
    /**
     * 组件销毁方法
     */
    beforeDestroy() {
        this.observer.unobserve(this.$refs.wrap);
        this.observer.unobserve(this.$refs.view);
        this.observer = null;
    },
    methods: {
        /**
         *Ssize改变事件
         * @param entries 变化列表
         */
        handelResize(entries) {
            this.update();
        },
        /**
         * 处理鼠标滚动事件
         */
        handleScroll() {
            const { wrap } = this;

            // 更新滑块位置
            this.moveY = (wrap.scrollTop * 100) / wrap.clientHeight;
            this.moveX = (wrap.scrollLeft * 100) / wrap.clientWidth;
        },
        /**
         * 更新滚动条滑块长度
         */
        update() {
            const { wrap } = this;
            if (!wrap) {
                return;
            }

            // 如果宽度或者高度为0,则不更新滚动条滑块长度
            if (wrap.clientWidth === 0 || wrap.clientHeight === 0) {
                return;
            }

            if (this.scrollbarX) {
                // 计算水平滑块长度
                let widthPercentage = (wrap.clientWidth * 100) / wrap.scrollWidth;
                widthPercentage = widthPercentage < 100 ? `${widthPercentage}%` : '';

                // 只有发生变化时才更新水平滑块长度
                if (this.sizeWidth !== widthPercentage) {
                    this.sizeWidth = widthPercentage;
                }
            }

            if (this.scrollbarY) {
                // 计算垂直滑块长度
                let heightPercentage = (wrap.clientHeight * 100) / wrap.scrollHeight;
                heightPercentage = heightPercentage < 100 ? `${heightPercentage}%` : '';

                // 只有发生变化时才更新垂直滑块长度
                if (this.sizeHeight !== heightPercentage) {
                    this.sizeHeight = heightPercentage;
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.sy-scrollbar {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &:active > .sy-scrollbar-bar,
    &:focus > .sy-scrollbar-bar,
    &:hover > .sy-scrollbar-bar {
        opacity: 1;
        transition: opacity 340ms ease-out;
    }

    .sy-scrollbar-wrap {
        width: 100%;
        height: 100%;
        overflow: scroll;
    }

    .sy-scrollbar-view {
        position: relative;
    }
}
</style>
